/**
 * App-icon badge capability (src/platform/).
 *
 * Contract: every capability has a working web implementation; native
 * branches (via isNativePlatform()) load Capacitor plugins dynamically and
 * MUST keep the web fallback. There is no app icon on web, so this is a
 * complete no-op there.
 *
 * The badge always reflects the pending-approvals count (#2573) — the same
 * number the sidebar/bell badge shows (`usePendingApprovalsCount`). Callers
 * set it whenever that count changes; a delivered push also sets it directly
 * via APNs' `aps.badge` (push_apns.py) so a backgrounded app updates without
 * any JS running.
 */

import { isNativePlatform } from './detection'

/**
 * Set the app-icon badge to an exact count. 0 clears it. No-op on web, and
 * until the owner has granted notifications: the plugin's `set` would otherwise
 * raise the OS permission prompt itself, ahead of the contextual nudge
 * (`pushPrompt.ts`).
 */
export async function setBadgeCount(count: number): Promise<void> {
  if (!isNativePlatform()) return
  try {
    const { Badge } = await import('@capawesome/capacitor-badge')
    if ((await Badge.checkPermissions()).display !== 'granted') return
    await Badge.set({ count: Math.max(0, count) })
  } catch {
    // Plugin unavailable — nothing to set.
  }
}
